// src/App.js
import React, { Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Header from "./components/header/header";
import Contact from "./pages/contact";
import BuyAndRent from "./pages/buy&rent";
import Homepage from "./pages/homepage";
import Product from "./pages/product";
import Footer from "./components/footer/footer";
import PrivacyNotice from "./pages/privacy_notice";
import RepairAndRestore from "./pages/repair&restore";
import AnalyseAndDocument from "./pages/analyse&document";
import Imprint from "./pages/imprint";
import Disclaimer from "./pages/disclaimer";
import NotFound from "./pages/notfound";
import CookiesDisclaimer from "./components/cookies-disclaimer/cookies_disclaimer"; // Import the CookiesDisclaimer component
import LanguageRoute from "./components/LanguageRoute"; // Import the LanguageRoute component
import { ResponsiveProvider } from "./contexts/ResponsiveContext"; // Import the ResponsiveProvider
import "./i18n";

// Import app-level translations
import appEnTranslations from "./translations/app_en.json";
import appDeTranslations from "./translations/app_de.json";
import notfoundEnTranslations from "./translations/notfound_en.json";
import notfoundDeTranslations from "./translations/notfound_de.json";

function App() {
  const { t, i18n } = useTranslation();

  // Add app-level translations
  i18n.addResourceBundle("en", "app", appEnTranslations, true, true);
  i18n.addResourceBundle("de", "app", appDeTranslations, true, true);
  i18n.addResourceBundle("en", "notfound", notfoundEnTranslations, true, true);
  i18n.addResourceBundle("de", "notfound", notfoundDeTranslations, true, true);

  return (
    <Suspense fallback={t("app:loading", "Loading...")}>
      <Router>
        <LanguageRoute>
          <ResponsiveProvider>
            <div className="App">
              <CookiesDisclaimer />
              <Header />
              <main>
                <Helmet>
                  <title>{t("app:page_title")}</title>
                  <meta charSet="utf-8" />
                  <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                  />
                  <meta property="og:type" content="website" />
                  <meta property="og:title" content={t("app:og_title")} />
                  <meta
                    property="og:description"
                    content={t("app:og_description")}
                  />
                  <meta property="og:image" content="/og-image.jpg" />
                  <meta property="og:url" content={t("app:og_url")} />
                </Helmet>

                <Routes>
                  {/* Default routes (German) */}
                  <Route path="/" element={<Homepage />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/buy&rent" element={<BuyAndRent />} />
                  <Route path="/product/:id" element={<Product />} />
                  <Route path="/privacy_notice" element={<PrivacyNotice />} />
                  <Route
                    path="/repair&restore"
                    element={<RepairAndRestore />}
                  />
                  <Route
                    path="/analyse&document"
                    element={<AnalyseAndDocument />}
                  />
                  <Route path="/imprint" element={<Imprint />} />
                  <Route path="/disclaimer" element={<Disclaimer />} />

                  {/* German routes */}
                  <Route path="/de" element={<Homepage />} />
                  <Route path="/de/" element={<Homepage />} />
                  <Route path="/de/contact" element={<Contact />} />
                  <Route path="/de/buy&rent" element={<BuyAndRent />} />
                  <Route path="/de/product/:id" element={<Product />} />
                  <Route
                    path="/de/privacy_notice"
                    element={<PrivacyNotice />}
                  />
                  <Route
                    path="/de/repair&restore"
                    element={<RepairAndRestore />}
                  />
                  <Route
                    path="/de/analyse&document"
                    element={<AnalyseAndDocument />}
                  />
                  <Route path="/de/imprint" element={<Imprint />} />
                  <Route path="/de/disclaimer" element={<Disclaimer />} />

                  {/* English routes */}
                  <Route path="/en" element={<Homepage />} />
                  <Route path="/en/" element={<Homepage />} />
                  <Route path="/en/contact" element={<Contact />} />
                  <Route path="/en/buy&rent" element={<BuyAndRent />} />
                  <Route path="/en/product/:id" element={<Product />} />
                  <Route
                    path="/en/privacy_notice"
                    element={<PrivacyNotice />}
                  />
                  <Route
                    path="/en/repair&restore"
                    element={<RepairAndRestore />}
                  />
                  <Route
                    path="/en/analyse&document"
                    element={<AnalyseAndDocument />}
                  />
                  <Route path="/en/imprint" element={<Imprint />} />
                  <Route path="/en/disclaimer" element={<Disclaimer />} />

                  {/* Catch-all route for 404 - must be last */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <Footer />
            </div>
          </ResponsiveProvider>
        </LanguageRoute>
      </Router>
    </Suspense>
  );
}

export default App;
